import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDropzone } from "react-dropzone";
import { toPng } from "html-to-image";
import pencilIcon from "../../assets/icons/pencil.svg";
import {
  UploadCloud,
  FileText,
  Search,
  RotateCcw,
  Download,
  Building2,
  Table2,
  Info,
  Layers3,
  ArrowUpDown,
  ArrowLeftRight,
  Trash2,
  X,
} from "lucide-react";

const errMsg = (e) =>
  e?.response?.data?.error || e?.message || "Request failed";

function friendlyNetworkError(e) {
  const status = e?.response?.status;
  if (!status && e?.message === "Network Error") {
    return "Network Error. Please try again later.";
  }
  return errMsg(e);
}

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

function fmt(v, digits = 2) {
  return typeof v === "number" && Number.isFinite(v) ? v.toFixed(digits) : "—";
}

function titleizeSlug(v) {
  return String(v || "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function displaySupplier(v) {
  return String(v || "") === "<UNKNOWN>" ? "Unspecified" : v;
}

function normaliseManufacturers(payload) {
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.manufacturers)
      ? payload.manufacturers
      : Array.isArray(payload?.companies)
        ? payload.companies
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

  return [
    ...new Set(
      raw
        .map((item) => {
          if (typeof item === "string") return item;
          return (
            item?.manufacturer ||
            item?.company ||
            item?.name ||
            item?.label ||
            ""
          );
        })
        .filter(Boolean),
    ),
  ];
}

function SelectField({ value, onChange, children, disabled }) {
  return (
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-400/70 disabled:bg-slate-50 disabled:opacity-60"
    >
      {children}
    </select>
  );
}

function NumberField({ value, onChange, disabled, step = 0.001 }) {
  return (
    <input
      type="number"
      step={step}
      value={value ?? ""}
      onChange={onChange}
      disabled={disabled}
      className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-400/70 disabled:bg-slate-50 disabled:opacity-60"
    />
  );
}

function sanitiseRowsForResolve(rows) {
  return rows.map((row) => ({
    row_key: row.row_key,
    raw_pdf_code: row.raw_pdf_code || "",
    source_window_id: row.source_window_id || "",
    source_window_description: row.source_window_description || "",
        source_glass: row.source_glass || "",
        source_supplier: row.source_supplier || "",
    source_product_type: row.source_product_type || "",
    source_u:
      row.source_u == null || row.source_u === "" ? null : Number(row.source_u),
    source_s:
      row.source_s == null || row.source_s === "" ? null : Number(row.source_s),
    product_type: row.product_type || "",
    u_mode: row.u_mode || "nearest",
    u_target:
      row.u_target === "" || row.u_target == null ? null : Number(row.u_target),
    s_mode: row.s_mode || "nearest",
    s_target:
      row.s_target === "" || row.s_target == null ? null : Number(row.s_target),
  }));
}

async function fetchManufacturers(listBasePath) {
  try {
    const liteResponse = await axiosInstance.get(
      `${listBasePath}/manufacturers-lite`,
    );
    const liteList = normaliseManufacturers(liteResponse.data);
    if (liteList.length > 0) {
      return liteList;
    }
  } catch (e) {
    // fall back to the heavier endpoint below
  }

  const { data } = await axiosInstance.get(`${listBasePath}/manufacturers`);
  return normaliseManufacturers(data);
}

async function scanCompanyPdf({ company, file, material = "all", basePath }) {
  const form = new FormData();
  form.append("file", file);
  form.append("material", material);

  const { data } = await axiosInstance.post(
    `${basePath}/${encodeURIComponent(company)}/scan-pdf/async`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );

  return data;
}

async function scanCompanyPdfSync({
  company,
  file,
  material = "all",
  basePath,
}) {
  const form = new FormData();
  form.append("file", file);
  form.append("material", material);

  const url = `${basePath}/${encodeURIComponent(company)}/scan-pdf`;
  const transientStatuses = new Set([502, 503, 504]);
  let lastError = null;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const { data } = await axiosInstance.post(url, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      if (!transientStatuses.has(status) || attempt === 1) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, 1200));
    }
  }

  throw lastError || new Error("PDF scan failed");
}

async function waitForJobResult(
  jobPath,
  { pollIntervalMs = 1200, timeoutMs = 20 * 60 * 1000 } = {},
) {
  const startedAt = Date.now();
  const transientStatuses = new Set([502, 503, 504]);
  let retryCount = 0;

  while (Date.now() - startedAt < timeoutMs) {
    try {
      const { data } = await axiosInstance.get(jobPath);
      const status = data?.status;

      if (status === "completed") {
        return data?.result;
      }

      if (status === "failed") {
        throw new Error(data?.error || "Background job failed");
      }

      retryCount = 0;
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    } catch (error) {
      const status = error?.response?.status;
      if (!transientStatuses.has(status) || retryCount >= 5) {
        throw error;
      }

      retryCount += 1;
      const backoffMs = Math.min(pollIntervalMs * (retryCount + 1), 5000);
      await new Promise((resolve) => setTimeout(resolve, backoffMs));
    }
  }

  throw new Error("Background job timed out");
}

function buildCompanyJobPath(jobId, scanBasePath) {
  if (String(scanBasePath || "").includes("/sponsor")) {
    return `/finder/public/jobs/${jobId}`;
  }

  return `/finder/jobs/${jobId}`;
}

async function resolveCompanyRows({
  company,
  rows,
  material = "all",
  basePath,
  replacements = {},
}) {
  const url = `${basePath}/${encodeURIComponent(company)}/resolve`;
  const transientStatuses = new Set([502, 503, 504]);
  let lastError = null;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const { data } = await axiosInstance.post(url, {
        rows,
        material,
        replacements,
      });
      return data;
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      if (!transientStatuses.has(status) || attempt === 1) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  throw lastError || new Error("Resolve request failed");
}

async function fetchAlternativeSystems({
  company,
  rows,
  material = "all",
  basePath,
}) {
  const url = `${basePath}/${encodeURIComponent(company)}/alternatives`;
  const { data } = await axiosInstance.post(url, {
    rows,
    material,
  });
  return data;
}

async function fetchAlternativeFilterOptions({
  company,
  material = "all",
  basePath,
}) {
  const url = `${basePath}/${encodeURIComponent(company)}/alternative-filters`;
  const { data } = await axiosInstance.get(url, { params: { material } });
  return data;
}

async function fetchFrameKeywordSettings() {
  const { data } = await axiosInstance.get("/user/settings/frame-keywords");
  return data;
}

const DEFAULT_BASE_PATH = "/finder/company";

export default function GlassFinderPerCompany({
  basePath = DEFAULT_BASE_PATH,
  initialCompany = "",
  hideCompanySelector = false,
  showToastContainer = true,
}) {
  const [pdfFile, setPdfFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadingManufacturers, setLoadingManufacturers] = useState(false);

  const [manufacturers, setManufacturers] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(initialCompany || "");

  const [manufacturer, setManufacturer] = useState("");
  const [codes, setCodes] = useState([]);
  const [unmatchedCodes, setUnmatchedCodes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [materialFilter, setMaterialFilter] = useState("all");
  const [availableProductTypes, setAvailableProductTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [alternativeSystemsOpen, setAlternativeSystemsOpen] = useState(false);
  const [alternativeRowKey, setAlternativeRowKey] = useState(null);
  const [alternativeCompany, setAlternativeCompany] = useState(
    initialCompany || "",
  );
  const [alternativeCompanyMenuOpen, setAlternativeCompanyMenuOpen] =
    useState(false);
  const [alternativeLoading, setAlternativeLoading] = useState(false);
  const [autoSelecting, setAutoSelecting] = useState(false);
  const [alternativeOptions, setAlternativeOptions] = useState({});
  const [alternativeFilterOptions, setAlternativeFilterOptions] = useState({
    suppliers: [],
    manufacturers: [],
  });
  const [alternativeSupplierFilter, setAlternativeSupplierFilter] = useState("all");
  const [frameKeywordDefaults, setFrameKeywordDefaults] = useState({});
  const [alternativeFrameKeyword, setAlternativeFrameKeyword] = useState("");
  const [alternativeGlassKeyword, setAlternativeGlassKeyword] = useState("");
  const [pendingReplacements, setPendingReplacements] = useState({});
  const [selectedReplacements, setSelectedReplacements] = useState({});
  const [removeConfirmRowKey, setRemoveConfirmRowKey] = useState(null);
  const [alternativeSort, setAlternativeSort] = useState("asc");

  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [selectedRegisterRow, setSelectedRegisterRow] = useState(null);

  const exportRef = useRef(null);

  const listBasePath = "/finder/company";
  // Local backend auth is bypassed on localhost, so use the normal company
  // route and avoid the sponsor-only manufacturer restriction.
  const scanBasePath = basePath;

  const openRegisterDialog = (row) => {
    setSelectedRegisterRow(row);
    setRegisterDialogOpen(true);
  };

  const closeRegisterDialog = () => {
    setRegisterDialogOpen(false);
    setSelectedRegisterRow(null);
  };

  useEffect(() => {
    fetchFrameKeywordSettings()
      .then((data) => setFrameKeywordDefaults(data?.defaults || {}))
      .catch(() => setFrameKeywordDefaults({}));
  }, []);

  useEffect(() => {
    const loadManufacturers = async () => {
      try {
        setLoadingManufacturers(true);
        const list = await fetchManufacturers(listBasePath);
        setManufacturers(list);

        if (list.length > 0) {
          if (hideCompanySelector) {
            setSelectedCompany(initialCompany);
          } else {
            setSelectedCompany((prev) =>
              prev && list.includes(prev) ? prev : list[0],
            );
          }
          setAlternativeCompany((prev) =>
            prev && list.includes(prev)
              ? prev
              : initialCompany && list.includes(initialCompany)
                ? initialCompany
                : list[0],
          );
        }
      } catch (e) {
        toast.error(`Failed to load companies: ${errMsg(e)}`);
      } finally {
        setLoadingManufacturers(false);
      }
    };

    loadManufacturers();
  }, [basePath, hideCompanySelector, initialCompany]);

  const companyTitle = useMemo(
    () => manufacturer || titleizeSlug(selectedCompany),
    [manufacturer, selectedCompany],
  );

  const alternativeSelectionActive =
    alternativeLoading ||
    (alternativeSystemsOpen &&
      alternativeRowKey &&
      !selectedReplacements[alternativeRowKey]);

  const handleDrop = React.useCallback((acceptedFiles, fileRejections) => {
    const rejected = fileRejections?.[0];
    if (rejected) {
      toast.error("Please drop a single PDF file");
      return;
    }

    const file = acceptedFiles?.[0];
    if (!file) return;

    const isPdf =
      file.type === "application/pdf" ||
      String(file.name || "")
        .toLowerCase()
        .endsWith(".pdf");

    if (!isPdf) {
      toast.error("Please select a PDF file");
      return;
    }

    setPdfFile(file);
  }, []);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({
    onDrop: handleDrop,
    accept: { "application/pdf": [".pdf"] },
    maxFiles: 1,
    multiple: false,
    noClick: true,
    noKeyboard: true,
  });

  const clearResolvedState = () => {
    setManufacturer("");
    setCodes([]);
    setUnmatchedCodes([]);
    setMaterials([]);
    setMaterialFilter("all");
    setAvailableProductTypes([]);
    setRows([]);
    setPreviewRows([]);
    setAlternativeSystemsOpen(false);
    setAlternativeRowKey(null);
    setAlternativeCompanyMenuOpen(false);
    setAlternativeCompany(selectedCompany);
    setAlternativeOptions({});
    setAlternativeFilterOptions({ suppliers: [], manufacturers: [] });
    setAlternativeSupplierFilter("all");
    setPendingReplacements({});
    setRemoveConfirmRowKey(null);
  };

  const resetAll = () => {
    setPdfFile(null);
    setProcessing(false);
    setApplying(false);
    setExporting(false);
    clearResolvedState();
  };

  const applyResponse = (data) => {
    setManufacturer(data?.manufacturer || "");
    setCodes(Array.isArray(data?.codes) ? data.codes : []);
    setUnmatchedCodes(
      Array.isArray(data?.unmatched_codes) ? data.unmatched_codes : [],
    );
    setMaterials(Array.isArray(data?.materials) ? data.materials : []);
    setMaterialFilter(data?.selected_material || "all");
    setAvailableProductTypes(
      Array.isArray(data?.available_product_types)
        ? data.available_product_types
        : [],
    );
    const nextRows = Array.isArray(data?.rows) ? data.rows : [];
    setRows(nextRows);
    setPreviewRows(nextRows);
  };

  const runScan = async () => {
    try {
      if (!selectedCompany) {
        toast.error("No company available");
        return;
      }

      if (!pdfFile) {
        toast.error("Select a PDF first");
        return;
      }

      setProcessing(true);

      toast.info("PDF received. Scanning now...");

      const data = await scanCompanyPdfSync({
        company: selectedCompany,
        file: pdfFile,
        material: materialFilter,
        basePath: scanBasePath,
      });
      applyResponse(data);
      await loadAlternativeFilterOptions(
        selectedCompany,
        data?.selected_material || materialFilter,
      );
      toast.success(
        `PDF scanned successfully for ${data?.manufacturer || selectedCompany}`,
      );
    } catch (e) {
      toast.error(`PDF scan failed: ${friendlyNetworkError(e)}`);
      clearResolvedState();
    } finally {
      setProcessing(false);
    }
  };

  const updateRow = (rowKey, patch) => {
    setRows((prev) =>
      prev.map((row) => (row.row_key === rowKey ? { ...row, ...patch } : row)),
    );
  };

  const loadAlternativeSystems = async (rowKey, companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company || rows.length === 0) {
      toast.info("Scan a PDF before selecting an alternative system");
      return;
    }

    if (!companyOverride) setAlternativeCompany(company);
    setAlternativeRowKey(rowKey || null);
    try {
      setAlternativeLoading(true);
      const row = rows.find((item) => item.row_key === rowKey);
      const keywordEntry = Object.entries(frameKeywordDefaults).find(
        ([productType]) =>
          productType.toLowerCase() ===
          String(row?.source_product_type || row?.product_type || "").toLowerCase(),
      );
      setAlternativeFrameKeyword(keywordEntry?.[1] || "");
      setAlternativeGlassKeyword("");
      const data = await fetchAlternativeSystems({
        company,
        rows: sanitiseRowsForResolve(row ? [row] : rows),
        material: materialFilter,
        basePath: scanBasePath,
      });
      setAlternativeOptions(data?.options_by_row_key || {});
      setAlternativeFilterOptions(
        data?.filter_options || { suppliers: [], manufacturers: [] },
      );
      setAlternativeSystemsOpen(true);
    } catch (e) {
      toast.error(`Failed to load alternatives: ${friendlyNetworkError(e)}`);
    } finally {
      setAlternativeLoading(false);
    }
  };

  const loadAlternativeFilterOptions = async (
    company = selectedCompany,
    material = materialFilter,
  ) => {
    if (!company) return;
    try {
      const data = await fetchAlternativeFilterOptions({
        company,
        material,
        basePath: scanBasePath,
      });
      setAlternativeFilterOptions(
        data?.filter_options || { suppliers: [], manufacturers: [] },
      );
    } catch (e) {
      setAlternativeFilterOptions({ suppliers: [], manufacturers: [] });
    }
  };

  const getVisibleAlternativeOptions = (rowKey) =>
    (alternativeOptions[rowKey] || []).filter(
      (option) =>
        (alternativeSupplierFilter === "all" ||
          option.supplier === alternativeSupplierFilter) &&
        (!alternativeFrameKeyword.trim() ||
          String(option.frame_desc || "")
            .toLowerCase()
            .includes(alternativeFrameKeyword.trim().toLowerCase())) &&
        (!alternativeGlassKeyword.trim() ||
          String(option.glass_description || option.glass || "")
            .toLowerCase()
            .includes(alternativeGlassKeyword.trim().toLowerCase())),
    );

  const chooseReplacement = (rowKey, replacement) => {
    setPendingReplacements((prev) => ({ ...prev, [rowKey]: replacement }));
  };

  const confirmReplacement = async (rowKey) => {
    const replacement = pendingReplacements[rowKey];
    if (!replacement) return;
    const nextReplacements = { ...selectedReplacements, [rowKey]: replacement };
    setSelectedReplacements(nextReplacements);
    setPendingReplacements((prev) => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setAlternativeSystemsOpen(false);
    await handleApply(rows, materialFilter, selectedCompany, nextReplacements);
  };

  const autoSelectBestFit = async () => {
    if (!selectedCompany || rows.length === 0) {
      toast.info("Scan a PDF before selecting the best-fit systems");
      return;
    }

    try {
      setAutoSelecting(true);
      const data = await fetchAlternativeSystems({
        company: selectedCompany,
        rows: sanitiseRowsForResolve(rows),
        material: materialFilter,
        basePath: scanBasePath,
      });
      const optionsByRowKey = data?.options_by_row_key || {};
      const nextReplacements = { ...selectedReplacements };
      let selectedCount = 0;

      rows.forEach((row) => {
        const keywordEntry = Object.entries(frameKeywordDefaults).find(
          ([productType]) =>
            productType.toLowerCase() ===
            String(row.source_product_type || row.product_type || "").toLowerCase(),
        );
        const defaultKeyword = String(keywordEntry?.[1] || "")
          .trim()
          .toLowerCase();
        const rowOptions = (optionsByRowKey[row.row_key] || []).filter(
          (option) =>
            !defaultKeyword ||
            String(option.frame_desc || "").toLowerCase().includes(defaultKeyword),
        );
        const extractedU = Number(row.source_u);
        const extractedShgc = Number(row.source_s);
        const hasExtractedU = Number.isFinite(extractedU);
        const hasExtractedShgc = Number.isFinite(extractedShgc);

        const bestOption = rowOptions.reduce((best, option) => {
          if (!best) return option;

          const optionU = Number(option.u_value);
          const bestU = Number(best.u_value);
          const optionUGap = hasExtractedU
            ? extractedU - optionU
            : Number.POSITIVE_INFINITY;
          const bestUGap = hasExtractedU
            ? extractedU - bestU
            : Number.POSITIVE_INFINITY;
          if (optionUGap < bestUGap) return option;
          if (optionUGap > bestUGap) return best;

          const optionShgcGap = hasExtractedShgc
            ? Math.abs(option.shgc - extractedShgc)
            : Number.POSITIVE_INFINITY;
          const bestShgcGap = hasExtractedShgc
            ? Math.abs(best.shgc - extractedShgc)
            : Number.POSITIVE_INFINITY;
          return optionShgcGap < bestShgcGap ? option : best;
        }, null);

        if (bestOption) {
          nextReplacements[row.row_key] = bestOption;
          selectedCount += 1;
        }
      });

      setSelectedReplacements(nextReplacements);
      setPendingReplacements({});
      await handleApply(rows, materialFilter, selectedCompany, nextReplacements);
      toast.success(
        selectedCount > 0
          ? `Auto-selected best fit for ${selectedCount} row${selectedCount === 1 ? "" : "s"}`
          : "No matching systems were found",
      );
    } catch (e) {
      toast.error(`Auto-select failed: ${friendlyNetworkError(e)}`);
    } finally {
      setAutoSelecting(false);
    }
  };

  const removeReplacement = async (rowKey) => {
    const nextReplacements = { ...selectedReplacements };
    delete nextReplacements[rowKey];
    setSelectedReplacements(nextReplacements);
    setPendingReplacements((prev) => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setRemoveConfirmRowKey(null);
    await handleApply(rows, materialFilter, selectedCompany, nextReplacements);
    await loadAlternativeSystems(rowKey);
  };

  const handleApply = async (
    nextRows = rows,
    nextMaterial = materialFilter,
    nextCompany = selectedCompany,
    nextReplacements = selectedReplacements,
  ) => {
    try {
      if (!nextCompany) {
        toast.error("No company available");
        return;
      }

      setApplying(true);

      const data = await resolveCompanyRows({
        company: nextCompany,
        rows: sanitiseRowsForResolve(nextRows),
        material: nextMaterial,
        basePath: scanBasePath,
        replacements: nextReplacements,
      });

      setManufacturer(data?.manufacturer || "");
      setMaterials(Array.isArray(data?.materials) ? data.materials : materials);
      setMaterialFilter(data?.selected_material || nextMaterial);
      setAvailableProductTypes(
        Array.isArray(data?.available_product_types)
          ? data.available_product_types
          : [],
      );
      setPreviewRows(Array.isArray(data?.rows) ? data.rows : []);
      toast.success("Table updated");
    } catch (e) {
      toast.error(`Failed to update table: ${friendlyNetworkError(e)}`);
    } finally {
      setApplying(false);
    }
  };

  const handleMaterialChange = async (e) => {
    const nextMaterial = e.target.value;
    setMaterialFilter(nextMaterial);
    setAlternativeOptions({});
    setAlternativeFilterOptions({ suppliers: [], manufacturers: [] });
    setPendingReplacements({});
    setSelectedReplacements({});
    setRemoveConfirmRowKey(null);
    setAlternativeSystemsOpen(false);
    setAlternativeRowKey(null);

    if (rows.length > 0) {
      await handleApply(rows, nextMaterial, selectedCompany);
    }
    await loadAlternativeFilterOptions(selectedCompany, nextMaterial);
  };

  const handleCompanyChange = async (e) => {
    if (hideCompanySelector) return;
    const nextCompany = e.target.value;
    setSelectedCompany(nextCompany);
    setAlternativeCompany(nextCompany);
    setManufacturer("");
    setAlternativeOptions({});
    setAlternativeFilterOptions({ suppliers: [], manufacturers: [] });
    setAlternativeSupplierFilter("all");
    setPendingReplacements({});
    setRemoveConfirmRowKey(null);
    setAlternativeSystemsOpen(false);
    setAlternativeRowKey(null);

    if (rows.length > 0) {
      await handleApply(rows, materialFilter, nextCompany);
    }
    await loadAlternativeFilterOptions(nextCompany, materialFilter);
  };

  const applyPresetToAll = async (preset) => {
    const updatedRows = rows.map((row) => ({
      ...row,
      u_mode: preset,
      s_mode: preset,
      u_target: row.source_u ?? row.u_target ?? "",
      s_target: row.source_s ?? row.s_target ?? "",
    }));

    setRows(updatedRows);
    await handleApply(updatedRows, materialFilter, selectedCompany);
  };

  const handleExport = async () => {
    if (!exportRef.current || finalisedPreviewRows.length === 0) return;

    try {
      setExporting(true);

      const dataUrl = await toPng(exportRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#ffffff",
      });

      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${(manufacturer || selectedCompany || "company")
        .toLowerCase()
        .replace(/\s+/g, "-")}-window-table.png`;
      a.click();
    } catch (e) {
      toast.error(`Export failed: ${friendlyNetworkError(e)}`);
    } finally {
      setExporting(false);
    }
  };

  const finalisedPreviewRows = previewRows.filter(
    (row) => row?.row_key && selectedReplacements[row.row_key],
  );
  const previewRowsByKey = new Map(
    previewRows.map((row) => [row.row_key, row]),
  );
  const previewDisplayRows = rows.map(
    (row) => previewRowsByKey.get(row.row_key) || { row_key: row.row_key },
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Upload */}
      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="px-5 py-5 sm:px-6 sm:py-6">
          <div className="flex items-start gap-4 text-left">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
              <Building2 className="h-5 w-5" />
            </div>

            <div className="min-w-0 w-full">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-slate-900">
                    {companyTitle || "Company"} - Company PDF Selector
                  </h2>
                  <p className="mt-1 text-xs sm:text-sm text-slate-500">
                    Upload a PDF, extract the window codes, then resolve
                    products only from this company.
                  </p>
                </div>

                {/* <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="slate">Company scope only</Badge>
                  <Badge tone="amber">Default scan: max</Badge>
                </div> */}
              </div>

              <input {...getInputProps()} />

              <div
                {...getRootProps()}
                className={cx(
                  "mt-5 rounded-2xl border-2 border-dashed p-6 sm:p-8 transition-colors bg-slate-50",
                  isDragAccept && "border-emerald-400 bg-emerald-50",
                  isDragReject && "border-rose-400 bg-rose-50",
                  isDragActive &&
                    !isDragReject &&
                    "border-slate-400 bg-slate-100",
                  !isDragActive &&
                    !isDragAccept &&
                    !isDragReject &&
                    "border-slate-300 hover:border-slate-400",
                )}
              >
                <div className="flex gap-12 lg:gap-32 flex-col lg:flex-row items-center justify-center">
                  <div className="flex flex-col items-center justify-center text-center lg:items-start lg:text-left">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm border border-slate-200">
                      <UploadCloud className="h-7 w-7 text-slate-700" />
                    </div>

                    <div className="mt-4">
                      <h3 className="text-sm sm:text-base font-bold text-slate-900">
                        {isDragActive
                          ? "Drop your PDF here"
                          : "Drag and drop your PDF here"}
                      </h3>
                      <p className="mt-1 text-xs sm:text-sm text-slate-500">
                        PDF only, one file at a time
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={open}
                      className="mt-4 h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-60"
                      disabled={processing || applying}
                    >
                      <span className="inline-flex items-center gap-2">
                        <FileText className="h-4 w-4 text-slate-400" />
                        {pdfFile ? "Replace PDF" : "Browse File"}
                      </span>
                    </button>
                  </div>

                  <div className="flex flex-col items-center text-center">
                    <img
                      src="/nathers_report_example.png"
                      alt="Example report"
                      className="w-full max-w-[320px] rounded-2xl border border-slate-200 bg-white object-contain shadow-sm"
                    />
                    <p className="mt-2 text-xs text-slate-500">
                      Example of the report format to upload
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={runScan}
                  className="h-11 rounded-xl bg-slate-900 px-6 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
                  disabled={
                    processing ||
                    applying ||
                    !pdfFile ||
                    !selectedCompany ||
                    loadingManufacturers
                  }
                >
                  <span className="inline-flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    {processing ? "Processing..." : "Scan PDF"}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={resetAll}
                  className="h-11 rounded-xl border border-slate-200 bg-white px-6 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-60"
                  disabled={processing || applying}
                >
                  <span className="inline-flex items-center gap-2">
                    <RotateCcw className="h-4 w-4 text-slate-400" />
                    Reset
                  </span>
                </button>
              </div>

              {selectedCompany && (
                <div className="mt-3 text-xs sm:text-sm text-slate-600">
                  Current company scope:{" "}
                  <span className="font-semibold text-slate-900">
                    {titleizeSlug(selectedCompany)}
                  </span>
                </div>
              )}

              {pdfFile && (
                <div className="mt-1 text-xs sm:text-sm text-slate-600">
                  Selected:{" "}
                  <span className="font-semibold text-slate-900">
                    {pdfFile.name}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Summary + Material */}
      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="px-5 py-5 sm:px-6 sm:py-6">
          <div className="flex items-start gap-4 text-left">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
              <Layers3 className="h-5 w-5" />
            </div>

            <div className="min-w-0 w-full">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                    Company Scope & Filters
                  </h3>
                  <p className="mt-1 text-xs sm:text-sm text-slate-500">
                    The table below only contains products from{" "}
                    <span className="font-semibold">{companyTitle}</span>. Use
                    the material filter to change the results.
                  </p>
                </div>

                {/* <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="slate">Codes: {codes.length}</Badge>
                  <Badge tone="emerald">Rows: {rows.length}</Badge>
                  <Badge tone="sky">Blue Glass = Exact Match</Badge>
                </div> */}
              </div>

              {unmatchedCodes.length > 0 && (
                <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                  Unmatched PDF codes: {unmatchedCodes.join(", ")}
                </div>
              )}

              <div
                className={cx(
                  "mt-5 grid grid-cols-1 gap-4 lg:grid-cols-5",
                  alternativeSelectionActive && "opacity-60",
                )}
              >
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs font-semibold text-slate-500">
                    Company
                  </div>
                  <div className="mt-2">
                    {hideCompanySelector ? (
                      <div className="flex h-11 items-center rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 shadow-sm">
                        {companyTitle ||
                          titleizeSlug(selectedCompany) ||
                          "Company"}
                      </div>
                    ) : (
                      <SelectField
                        value={selectedCompany}
                        onChange={handleCompanyChange}
                        disabled={
                          processing ||
                          applying ||
                          loadingManufacturers ||
                          alternativeSelectionActive
                        }
                      >
                        {manufacturers.map((m) => (
                          <option key={m} value={m}>
                            {titleizeSlug(m)}
                          </option>
                        ))}
                      </SelectField>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs font-semibold text-slate-500">
                    Glass Supplier
                  </div>
                  <div className="mt-2">
                    <SelectField
                      value={alternativeSupplierFilter}
                      onChange={(e) => setAlternativeSupplierFilter(e.target.value)}
                      disabled={processing || applying || alternativeSelectionActive}
                    >
                      <option value="all">All Suppliers</option>
                      {alternativeFilterOptions.suppliers.map((supplier) => (
                        <option key={supplier} value={supplier} className="hover:bg-blue-100 hover:text-blue-900">
                          {displaySupplier(supplier)}
                        </option>
                      ))}
                    </SelectField>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs font-semibold text-slate-500">
                    Material
                  </div>
                  <div className="mt-2">
                    <SelectField
                      value={materialFilter}
                      onChange={handleMaterialChange}
                      disabled={processing || applying || alternativeSelectionActive}
                    >
                      <option value="all">All Materials</option>
                      {materials.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </SelectField>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs font-semibold text-slate-500">
                    Initial Set
                  </div>
                  <div className="mt-1 text-sm font-bold text-slate-900">
                    MAX
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs font-semibold text-slate-500">
                    SHGC Tolerance
                  </div>
                  <div className="mt-1 text-sm font-bold text-slate-900">
                    ±0.02
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Controls */}
      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="px-5 py-5 sm:px-6 sm:py-6">
          <div className="flex items-start gap-4 text-left">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
              <Info className="h-5 w-5" />
            </div>

            <div className="min-w-0 w-full">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                    Selection Controls
                  </h3>
                  <p className="mt-1 text-xs sm:text-sm text-slate-500">
                    Change product type, U selector, or SHGC selector, then
                    apply the selections.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={autoSelectBestFit}
                    className="h-10 rounded-lg border border-slate-300 bg-slate-200 px-4 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-300 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={
                      processing ||
                      applying ||
                      autoSelecting ||
                      alternativeSelectionActive ||
                      rows.length === 0
                    }
                  >
                    {autoSelecting ? "Selecting..." : "Auto Select Best Fit"}
                  </button>

                  {/* <button
                    type="button"
                    onClick={() => applyPresetToAll("max")}
                    className="h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-60"
                    disabled={processing || applying}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Search className="h-4 w-4" />
                      {applying ? "Applying..." : "Apply max set"}
                    </span>
                  </button> */}

                  {/* <button
                    type="button"
                    onClick={() => applyPresetToAll("min")}
                    className="h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-60"
                    disabled={processing || applying}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Search className="h-4 w-4" />
                      {applying ? "Applying..." : "Apply min set"}
                    </span>
                  </button> */}

                  {/* <button
                    type="button"
                    onClick={() =>
                      handleApply(rows, materialFilter, selectedCompany)
                    }
                    className="h-11 rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
                    disabled={processing || applying}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Search className="h-4 w-4 text-slate-400" />
                      {applying ? "Updating..." : "Update"}
                    </span>
                  </button> */}

                </div>
              </div>

              {alternativeSystemsOpen && (
                <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h4 className="text-sm font-bold text-blue-950">
                        Alternative Systems
                      </h4>
                      <p className="mt-1 text-xs text-blue-800">
                        Options use the selected manufacturer, extracted product
                        type, u-value ceiling, and SHGC range of ±0.02.
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() =>
                            setAlternativeCompanyMenuOpen((open) => !open)
                          }
                          className="inline-flex min-h-8 max-w-[230px] items-center gap-2 rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-xs font-bold text-blue-950 shadow-sm hover:bg-blue-100"
                          aria-haspopup="listbox"
                          aria-expanded={alternativeCompanyMenuOpen}
                        >
                          <span className="truncate">
                            {titleizeSlug(alternativeCompany || selectedCompany) ||
                              "Company"}
                          </span>
                          <span aria-hidden="true">▾</span>
                        </button>
                        {alternativeCompanyMenuOpen && (
                          <div
                            className="absolute right-0 top-full z-30 mt-2 max-h-64 w-64 overflow-auto rounded-lg border border-slate-200 bg-white p-1 text-left shadow-xl"
                            role="listbox"
                            aria-label="Alternative systems company"
                          >
                            {manufacturers.map((companyOption) => (
                              <button
                                key={companyOption}
                                type="button"
                                onClick={async () => {
                                  setAlternativeCompany(companyOption);
                                  setAlternativeCompanyMenuOpen(false);
                                  setAlternativeSupplierFilter("all");
                                  if (alternativeRowKey) {
                                    await loadAlternativeSystems(
                                      alternativeRowKey,
                                      companyOption,
                                    );
                                  }
                                }}
                                className={cx(
                                  "block w-full rounded-md px-3 py-2 text-left text-xs hover:bg-blue-100 hover:text-blue-950",
                                  alternativeCompany === companyOption &&
                                    "bg-blue-50 font-bold text-blue-950",
                                )}
                                role="option"
                                aria-selected={alternativeCompany === companyOption}
                              >
                                {titleizeSlug(companyOption)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setAlternativeSystemsOpen(false);
                          setAlternativeRowKey(null);
                          setAlternativeOptions({});
                          setAlternativeCompanyMenuOpen(false);
                          setPendingReplacements((prev) => {
                            const next = { ...prev };
                            if (alternativeRowKey) delete next[alternativeRowKey];
                            return next;
                          });
                        }}
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-blue-950 hover:bg-blue-200"
                        aria-label="Close alternative systems"
                        title="Close alternative systems"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 rounded-xl border border-blue-200 bg-white/70 p-3">
                    <div className="grid gap-3 md:grid-cols-2">
                      <label className="text-xs font-semibold text-blue-950">
                        Frame Description Keyword
                        <input
                          type="text"
                          value={alternativeFrameKeyword}
                          onChange={(event) =>
                            setAlternativeFrameKeyword(event.target.value)
                          }
                          placeholder="Filter frame descriptions"
                          className="mt-1 h-9 w-full rounded-lg border border-blue-200 bg-white px-3 text-sm font-normal text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        />
                      </label>
                      <label className="text-xs font-semibold text-blue-950">
                        Glass Description Search
                        <input
                          type="text"
                          value={alternativeGlassKeyword}
                          onChange={(event) =>
                            setAlternativeGlassKeyword(event.target.value)
                          }
                          placeholder="Filter glass descriptions"
                          className="mt-1 h-9 w-full rounded-lg border border-blue-200 bg-white px-3 text-sm font-normal text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        />
                      </label>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setAlternativeFrameKeyword("")}
                        disabled={!alternativeFrameKeyword}
                        className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                      >
                        Clear frame keyword
                      </button>
                      <button
                        type="button"
                        onClick={() => setAlternativeGlassKeyword("")}
                        disabled={!alternativeGlassKeyword}
                        className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                      >
                        Clear glass search
                      </button>
                    </div>
                    <p className="mt-2 text-[11px] text-blue-800">
                      This starts with your saved product-type default and can
                      be changed or cleared for this alternative search.
                    </p>
                  </div>

                  <div className="mt-3 space-y-4">
                    {rows
                      .filter((row) => row.row_key === alternativeRowKey)
                      .map((row, idx) => {
                      const rowKey = row.row_key || `row-${idx + 1}`;
                      const options = [...getVisibleAlternativeOptions(rowKey)].sort(
                        (a, b) =>
                          (Number(a.u_value) - Number(b.u_value)) *
                          (alternativeSort === "asc" ? 1 : -1),
                      );
                      return (
                        <div
                          key={rowKey}
                          className="overflow-hidden rounded-xl border border-blue-200 bg-white"
                        >
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-blue-100 bg-blue-100/60 px-3 py-2 text-xs text-blue-950">
                            <span className="font-bold">
                              {row.source_window_id || "Extracted row"}
                            </span>
                            <span>{row.source_product_type || "—"}</span>
                            <span>U ≤ {fmt(row.source_u, 3)}</span>
                            <span>Extracted SHGC {fmt(row.source_s, 3)}</span>
                          </div>
                          <div className="max-h-[350px] overflow-auto">
                            <table className="min-w-[1100px] w-full border-collapse text-xs">
                              <thead className="sticky top-0 z-10 bg-slate-50 text-left text-[10px] font-bold uppercase tracking-wide text-slate-500">
                                <tr>
                                  <th className="px-3 py-2">Manufacturer</th>
                                  <th className="w-[170px] min-w-[170px] px-3 py-2">Product type</th>
                                  <th className="px-3 py-2">Frame description</th>
                                  <th className="px-3 py-2">Glass description</th>
                                  <th className="px-3 py-2">Glass supplier</th>
                                  <th className="w-[120px] min-w-[120px] px-3 py-2">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setAlternativeSort((prev) =>
                                          prev === "asc" ? "desc" : "asc",
                                        )
                                      }
                                      className={cx(
                                        "-mx-2 inline-flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-slate-100",
                                        alternativeSort && "bg-slate-100",
                                      )}
                                      title="Sort U value"
                                    >
                                      <span>U Value</span>
                                      <ArrowUpDown className="h-4 w-4 text-slate-700" />
                                      <span className="sr-only">
                                        {alternativeSort === "asc"
                                          ? "sorted low to high"
                                          : "sorted high to low"}
                                      </span>
                                    </button>
                                  </th>
                                  <th className="w-[110px] min-w-[110px] px-3 py-2">SHGC</th>
                                  <th className="w-[160px] min-w-[160px] px-3 py-2">Window code</th>
                                  <th className="px-3 py-2">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {options.length > 0 ? (
                                  options.map((option) => {
                                    const chosen =
                                      (pendingReplacements[rowKey] ||
                                        selectedReplacements[rowKey])?.window_code ===
                                      option.window_code;
                                    return (
                                      <tr
                                        key={option.window_code}
                                        className={cx(
                                          "border-t border-slate-100",
                                          chosen && "bg-blue-100/70",
                                        )}
                                      >
                                        <td className="px-3 py-2">{option.manufacturer || "—"}</td>
                                        <td className="w-[170px] min-w-[170px] px-3 py-2">{option.product_type || "—"}</td>
                                        <td className="px-3 py-2">{option.frame_desc || "—"}</td>
                                        <td className="px-3 py-2">{option.glass_description || "—"}</td>
                                        <td className="px-3 py-2">{displaySupplier(option.supplier) || "—"}</td>
                                        <td className="w-[120px] min-w-[120px] px-3 py-2">{fmt(option.u_value, 3)}</td>
                                        <td className="w-[110px] min-w-[110px] px-3 py-2">{fmt(option.shgc, 3)}</td>
                                        <td className="w-[160px] min-w-[160px] px-3 py-2 font-semibold">{option.window_code || "—"}</td>
                                        <td className="px-3 py-2">
                                          <button
                                            type="button"
                                            onClick={() => chooseReplacement(rowKey, option)}
                                            className="rounded-lg bg-[#075bdb] px-3 py-1.5 font-semibold text-white hover:bg-[#064bb5]"
                                          >
                                            {chosen ? "Selected" : "Select"}
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td colSpan={9} className="px-3 py-4 text-center text-slate-500">
                                      No alternatives match this extracted row.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                      })}
                  </div>
                </div>
              )}

              <div className="mt-4 grid grid-cols-1 gap-5 xl:grid-cols-2">
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                    <h4 className="text-sm font-bold text-slate-900">
                      Extracted PDF Data
                    </h4>
                    <p className="mt-1 text-xs text-slate-500">
                      Values read directly from the uploaded PDF.
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-[680px] w-full table-fixed border-collapse">
                      <thead className="bg-slate-50">
                        <tr className="border-b border-slate-200">
                          <th className="w-[125px] min-w-[125px] px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">Window code</th>
                          <th className="w-[155px] min-w-[155px] px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">Product type</th>
                          <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">Glass description</th>
                          <th className="w-[90px] min-w-[90px] px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">Target U</th>
                          <th className="w-[145px] min-w-[145px] px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">SHGC range</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length > 0 ? rows.map((row, idx) => (
                          <tr key={row.row_key || idx} className="border-b border-slate-200 last:border-b-0">
                            <td className="px-3 py-3 text-sm text-slate-700">{row.source_window_id || "—"}</td>
                            <td className="px-3 py-3 text-sm text-slate-700">{row.source_product_type || row.product_type || "—"}</td>
                            <td className="px-3 py-3 text-sm text-slate-700">{row.source_glass || "—"}</td>
                            <td className="px-3 py-3 text-sm text-slate-700">{row.source_u == null ? "—" : Number(row.source_u).toFixed(3)}</td>
                            <td className="px-3 py-3 text-sm text-slate-700">
                              {row.source_s == null ? "—" : `${(Number(row.source_s) - 0.02).toFixed(3)} to ${(Number(row.source_s) + 0.02).toFixed(3)}`}
                            </td>
                          </tr>
                        )) : (
                          <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-500">No extracted rows yet.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h4 className="text-sm font-bold text-slate-900">
                        Finalised Data
                      </h4>
                      <p className="mt-1 text-xs text-slate-500">
                        The selected values used for the table preview after update.
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-semibold text-slate-600">
                      <ArrowLeftRight className="h-3.5 w-3.5" />
                      Scroll horizontally
                    </span>
                  </div>
                </div>
                <div className="overflow-x-scroll">
                    <table className="min-w-[850px] w-full table-fixed border-collapse">
                    <thead className="bg-slate-50">
                      <tr className="border-b border-slate-200">
                        <th className="w-[100px] min-w-[100px] px-3 py-3 text-center text-xs font-bold uppercase tracking-wide text-slate-500">
                        </th>
                        <th className="w-[120px] min-w-[120px] px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                          Window code
                        </th>
                        {/* Manufacturer hidden temporarily for selection testing. */}
                        {/*
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                          Manufacturer
                        </th>
                        */}
                        <th className="w-[150px] min-w-[150px] px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                          Product type
                        </th>
                        {/* Frame description hidden temporarily for selection testing. */}
                        {/*
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                          Frame description
                        </th>
                        */}
                        <th className="w-[180px] min-w-[180px] px-4 py-3 pl-5 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                        Glass description
                        </th>
                        {/* Glass supplier and source U value hidden temporarily for selection testing. */}
                        {/*
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                          Glass supplier
                        </th>
                        <th className="w-[130px] min-w-[130px] px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                          U value
                        </th>
                        */}
                        <th className="w-[100px] min-w-[100px] px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                          Target U
                        </th>
                        <th className="w-[100px] min-w-[100px] px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                          SHGC range
                        </th>
                        {/* U selector and SHGC selector hidden temporarily for selection testing. */}
                        {/*
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                          U selector
                        </th>
                        <th className="w-[150px] min-w-[150px] px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                          SHGC selector
                        </th>
                        */}
                      </tr>
                    </thead>

                    <tbody>
                      {rows.length > 0 ? (
                        rows.map((row, idx) => (
                          <React.Fragment key={row.row_key || idx}>
                            <tr className="border-b border-slate-200">
                            <td className="w-[100px] min-w-[100px] px-3 py-3 align-middle">
                              <div className="relative flex min-w-[88px] items-center justify-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => loadAlternativeSystems(row.row_key)}
                                  className={cx(
                                    "relative z-10 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg p-2 text-sm font-semibold text-white shadow-sm disabled:opacity-60",
                                    alternativeRowKey === row.row_key && alternativeSystemsOpen
                                      ? "bg-[#0b1f3a] hover:bg-[#061326]"
                                      : "bg-[#075bdb] hover:bg-[#064bb5]",
                                  )}
                                  disabled={
                                    processing ||
                                    applying ||
                                    (alternativeLoading && alternativeRowKey === row.row_key)
                                  }
                                >
                                  <img
                                    src={pencilIcon}
                                    alt=""
                                    aria-hidden="true"
                                    className="mx-auto h-5 w-5"
                                  />
                                  <span className="sr-only">
                                    {alternativeLoading && alternativeRowKey === row.row_key
                                      ? "Loading alternative systems"
                                      : selectedReplacements[row.row_key]
                                        ? "Edit selection"
                                        : "Select alternative system"}
                                  </span>
                                </button>
                                {selectedReplacements[row.row_key] && (
                                  <button
                                    type="button"
                                    onClick={() => setRemoveConfirmRowKey(row.row_key)}
                                    className="order-first inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-red-200 bg-white text-red-700 shadow-sm hover:bg-red-50 disabled:opacity-60"
                                    disabled={processing || applying}
                                    aria-label="Delete selected alternative"
                                    title="Delete selected alternative"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                                {removeConfirmRowKey === row.row_key && (
                                  <div className="absolute left-0 top-full z-20 mt-2 w-64 rounded-lg border border-slate-200 bg-white p-3 text-left text-xs text-slate-800 shadow-xl">
                                    <p className="font-semibold">
                                      Are you sure you want to delete this selection?
                                    </p>
                                    <div className="mt-2 flex justify-end gap-2">
                                      <button
                                        type="button"
                                        onClick={() => removeReplacement(row.row_key)}
                                        className="rounded-md bg-red-600 px-2.5 py-1 font-semibold text-white hover:bg-red-700"
                                      >
                                        Yes
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setRemoveConfirmRowKey(null)}
                                        className="rounded-md border border-slate-300 bg-white px-2.5 py-1 font-semibold text-slate-700 hover:bg-slate-100"
                                      >
                                        No
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="w-[100px] min-w-[10px] px-3 py-3 align-middle text-sm text-slate-700">
                              {selectedReplacements[row.row_key]?.window_code || "—"}
                            </td>

                            {/* Manufacturer hidden temporarily for selection testing. */}
                            {/*
                            <td className="px-3 py-3 align-middle">
                              <div className="flex items-center">
                                <div className="min-w-[220px] w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-600">
                                  <div className="font-semibold text-slate-900">
                                    {companyTitle || "—"}
                                  </div>
                                </div>
                              </div>
                            </td>
                            */}

                            <td className="px-3 py-3 align-middle text-sm text-slate-700">
                              {selectedReplacements[row.row_key]?.product_type || "—"}
                            </td>

                            {/* Frame description hidden temporarily for selection testing. */}
                            {/*
                            <td className="px-3 py-3 align-middle text-sm text-slate-700">
                              {row.source_window_description || "—"}
                            </td>
                            */}

                            <td className="px-3 py-3 pl-5 align-middle text-sm text-slate-700">
                              {selectedReplacements[row.row_key]?.glass_description || "—"}
                            </td>

                            {/* Glass supplier and source U value hidden temporarily for selection testing. */}
                            {/*
                            <td className="px-3 py-3 align-middle text-sm text-slate-700">
                              {row.source_supplier || "—"}
                            </td>

                            <td className="w-[130px] min-w-[130px] px-3 py-3 align-middle text-sm text-slate-700">
                              {row.source_u == null
                                ? "—"
                                : Number(row.source_u).toFixed(3)}
                            </td>
                            */}

                            <td className="w-[130px] min-w-[130px] px-3 py-3 align-middle text-sm text-slate-700">
                              {selectedReplacements[row.row_key]?.u_value == null
                                ? "—"
                                : Number(selectedReplacements[row.row_key].u_value).toFixed(3)}
                            </td>

                            <td className="w-[190px] min-w-[190px] px-3 py-3 align-middle text-sm text-slate-700">
                              {selectedReplacements[row.row_key]?.shgc == null
                                ? "—"
                                : `${(Number(selectedReplacements[row.row_key].shgc) - 0.02).toFixed(3)} to ${(Number(selectedReplacements[row.row_key].shgc) + 0.02).toFixed(3)}`}
                            </td>

                            {/* U selector hidden temporarily for selection testing. */}
                            {/*
                            <td className="px-3 py-3 align-middle">
                              <div className="flex items-center">
                                <div className="min-w-[220px] w-full flex gap-1">
                                  <label className="sr-only">U selector</label>
                                  <NumberField
                                    value={row.u_target ?? ""}
                                    onChange={(e) =>
                                      updateRow(row.row_key, {
                                        u_target: e.target.value,
                                      })
                                    }
                                    disabled={
                                      processing ||
                                      applying ||
                                      row.u_mode !== "nearest"
                                    }
                                  />
                                  <SelectField
                                    value={row.u_mode || "nearest"}
                                    onChange={(e) =>
                                      updateRow(row.row_key, {
                                        u_mode: e.target.value,
                                      })
                                    }
                                    disabled={processing || applying}
                                  >
                                    <option value="max">Max</option>
                                    <option value="min">Min</option>
                                    <option value="nearest">Nearest</option>
                                  </SelectField>
                                </div>
                              </div>
                            </td>

                            <td className="px-3 py-3 align-middle">
                              <div className="flex items-center">
                                <div className="min-w-[220px] w-full flex gap-1">
                                  <label className="sr-only">
                                    SHGC selector
                                  </label>
                                  <NumberField
                                    value={row.s_target ?? ""}
                                    onChange={(e) =>
                                      updateRow(row.row_key, {
                                        s_target: e.target.value,
                                      })
                                    }
                                    disabled={
                                      processing ||
                                      applying ||
                                      row.s_mode !== "nearest"
                                    }
                                  />
                                  <SelectField
                                    value={row.s_mode || "nearest"}
                                    onChange={(e) => {
                                      const nextMode = e.target.value;
                                      updateRow(row.row_key, {
                                        s_mode: nextMode,
                                        s_target:
                                          nextMode === "unlimited"
                                            ? ""
                                            : (row.s_target ??
                                              row.source_s ??
                                              ""),
                                      });
                                    }}
                                    disabled={processing || applying}
                                  >
                                    <option value="max">Max</option>
                                    <option value="min">Min</option>
                                    <option value="nearest">Nearest</option>
                                    <option value="unlimited">Unlimited</option>
                                  </SelectField>
                                </div>
                              </div>
                            </td>
                            */}
                            </tr>
                            {pendingReplacements[row.row_key] && (
                              <tr className="border-b border-blue-200 bg-blue-50">
                            <td colSpan={6} className="max-w-0 overflow-hidden px-4 py-2">
                                  <div className="flex w-full min-w-0 flex-wrap items-center gap-3 overflow-hidden text-xs text-blue-950">
                                    <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 overflow-hidden">
                                      <button
                                        type="button"
                                        onClick={() => confirmReplacement(row.row_key)}
                                        className="rounded-md bg-[#075bdb] px-2 py-1 font-bold text-white hover:bg-[#064bb5]"
                                      >
                                        Choose System
                                      </button>
                                      <span className="break-all font-semibold">
                                        {pendingReplacements[row.row_key].window_code}
                                      </span>
                                      <span className="break-all">
                                        {pendingReplacements[row.row_key].frame_desc || "—"}
                                      </span>
                                      <span>
                                        U {fmt(pendingReplacements[row.row_key].u_value, 3)}
                                      </span>
                                      <span>
                                        SHGC {fmt(pendingReplacements[row.row_key].shgc, 3)}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      ) : (
                        <tr className="border-b border-slate-200 last:border-b-0">
                          <td
                            colSpan={6}
                            className="px-3 py-8 text-center text-sm text-slate-500"
                          >
                            No resolved rows yet. You can still adjust filters
                            and rerun the scan.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              </div>

              <div className="mt-3 text-xs sm:text-sm text-slate-600">
                All resolved products come only from{" "}
                <span className="font-semibold">{companyTitle}</span>
                {materialFilter !== "all" ? (
                  <>
                    {" "}
                    with material{" "}
                    <span className="font-semibold">{materialFilter}</span>
                  </>
                ) : (
                  <> across all materials</>
                )}
                .
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Export / Preview table */}
      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="px-5 py-5 sm:px-6 sm:py-6">
          <div className="flex items-start gap-4 text-left">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
              <Table2 className="h-5 w-5" />
            </div>

            <div className="min-w-0 w-full">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                    Table Preview & Export
                  </h3>
                  <p className="mt-1 text-xs sm:text-sm text-slate-500">
                    You can export the table as an image to share with others.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleExport}
                  className="h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-60"
                  disabled={
                    processing ||
                    applying ||
                    exporting ||
                    finalisedPreviewRows.length === 0
                  }
                >
                  <span className="inline-flex items-center gap-2">
                    <Download className="h-4 w-4 text-slate-400" />
                    {exporting ? "Exporting..." : "Export Table As Image"}
                  </span>
                </button>
              </div>

              <div className="mt-4 overflow-hidden bg-[#ffffff]">
                <div
                  className="grid grid-cols-[28px_minmax(0,1fr)_28px]"
                  ref={exportRef}
                >
                  <div className="bg-[#cbddd0]" />
                  <div
                    className="bg-white px-6 py-5"
                    style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
                  >
                    <div>
                      <div className="mb-3 text-sm font-extrabold text-[#286f40]">
                        Custom* Windows
                      </div>

                      <table className="w-full table-fixed border-collapse text-left text-[12px] leading-[1.35] text-slate-900">
                        <thead>
                          <tr>
                            <th
                              rowSpan={2}
                              className="border-b border-[#B9B9B9] px-3 py-2 font-extrabold align-middle w-[110px]"
                            >
                              Window ID
                            </th>
                            <th
                              rowSpan={2}
                              className="border-b border-[#B9B9B9] px-3 py-2 font-extrabold align-middle w-[230px]"
                            >
                              Window Description
                            </th>
                            <th
                              rowSpan={2}
                              className="border-b border-[#B9B9B9] px-3 py-2 font-extrabold text-center align-middle w-[95px]"
                            >
                              Maximum
                              <br />
                              U-value*
                            </th>
                            <th
                              rowSpan={2}
                              className="border-b border-[#B9B9B9] px-3 py-2 font-extrabold text-center align-middle w-[70px]"
                            >
                              SHGC*
                            </th>
                            <th
                              colSpan={2}
                              className="border-b border-[#B9B9B9] px-3 py-2 font-extrabold text-center w-[150px]"
                            >
                              SHGC substitution
                              <br />
                              tolerance ranges
                            </th>
                            <th
                              rowSpan={2}
                              className="border-b border-[#B9B9B9] px-3 py-2 font-extrabold align-middle w-[120px]"
                            >
                              Product Type
                            </th>
                            <th
                              rowSpan={2}
                              className="border-b border-[#B9B9B9] px-3 py-2 font-extrabold align-middle w-[120px]"
                            >
                              Glass
                            </th>
                          </tr>
                          <tr>
                            <th className="border-b border-[#B9B9B9] px-3 py-1 font-bold text-center w-[75px]">
                              lower limit
                            </th>
                            <th className="border-b border-[#B9B9B9] px-3 py-1 font-bold text-center w-[75px]">
                              upper limit
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                      {previewDisplayRows.length > 0 ? (
                        previewDisplayRows.map((row, idx) => (
                              <tr key={row.row_key || idx}>
                                <td className="border-b border-[#CFCFCF] px-3 py-2 align-top font-normal">
                                  {row.window_id || "—"}
                                </td>
                                <td className="border-b border-[#CFCFCF] px-3 py-2 align-top">
                                  {row.window_description || "—"}
                                </td>
                                <td className="border-b border-[#CFCFCF] px-3 py-2 align-top text-center">
                                  {fmt(row.u_value, 1)}
                                </td>
                                <td className="border-b border-[#CFCFCF] px-3 py-2 align-top text-center">
                                  {fmt(row.shgc, 2)}
                                </td>
                                <td className="border-b border-[#CFCFCF] px-3 py-2 align-top text-center">
                                  {fmt(row.lower_limit, 2)}
                                </td>
                                <td className="border-b border-[#CFCFCF] px-3 py-2 align-top text-center">
                                  {fmt(row.upper_limit, 2)}
                                </td>
                                <td className="border-b border-[#CFCFCF] px-3 py-2 align-top">
                                  {row.product_type || "—"}
                                </td>
                                <td className="border-b border-[#CFCFCF] px-3 py-2 align-top">
                                  {!row.glass_eligible && row.glass ? (
                                    <button
                                      type="button"
                                      onClick={() => openRegisterDialog(row)}
                                      className="font-medium text-blue-700 hover:text-blue-800"
                                    >
                                      {row.glass}
                                    </button>
                      ) : (
                                    <span className="text-slate-900">
                                      {row.glass || "—"}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                className="border-b border-[#CFCFCF] px-2 py-8 text-center text-slate-500"
                                colSpan={8}
                              >
                                No extracted rows to preview yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      <p className="mt-3 text-[11px] font-medium text-slate-600">
                        This table is predictive only. For more accurate
                        results, please perform a proper simulation.
                      </p>
                    </div>
                  </div>
                  <div className="bg-[#cbddd0]" />
                </div>
              </div>

              <div className="mt-3 text-xs sm:text-sm text-slate-600">
                <p className="text-blue-700">
                  Blue glass means the closest company row within the current
                  active limits and material scope.
                  <span className="font-semibold">
                    {" "}
                    Click on the glass name for more details
                  </span>
                </p>
                <p className="text-slate-700">
                  Black glass means the resolved company row exactly matched the
                  requested product type, U, and SHGC.
                  <br />
                  If SHGC is set to{" "}
                  <span className="font-semibold">Unlimited</span>, SHGC is
                  ignored and matching is based on product type and U only.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {registerDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="text-lg font-bold text-slate-900 text-left">
                  System registration required
                </h4>
                <p className="mt-2 text-sm leading-6 text-slate-600 text-left">
                  This glazing/system combination may require registration
                  before it can be used. Please contact{" "}
                  <a
                    href="mailto:sales@ciilock.com"
                    className="font-semibold text-blue-700 underline underline-offset-2"
                  >
                    sales@ciilock.com
                  </a>{" "}
                  to arrange registration and receive the next steps.
                </p>

                {selectedRegisterRow && (
                  <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 text-left">
                    <div>
                      <span className="font-semibold text-slate-900">
                        Window ID:
                      </span>{" "}
                      {selectedRegisterRow.window_id || "—"}
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">
                        Window description:
                      </span>{" "}
                      {selectedRegisterRow.window_description || "—"}
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">
                        Product type:
                      </span>{" "}
                      {selectedRegisterRow.product_type || "—"}
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">
                        Glass:
                      </span>{" "}
                      {selectedRegisterRow.glass || "—"}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeRegisterDialog}
                className="h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100"
              >
                Close
              </button>
              <a
                href="mailto:sales@ciilock.com"
                className="inline-flex h-11 items-center rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
              >
                Email sales
              </a>
            </div>
          </div>
        </div>
      )}
      {showToastContainer && (
        <ToastContainer position="top-right" autoClose={4000} newestOnTop />
      )}
    </div>
  );
}
